import React, { useMemo, useState } from "react";
import { Field, useFormikContext } from "formik";
import Select from "../Select/Select";
import styles from "./RaciAssignment.module.scss";
import { ChevronDown, ChevronUp, UserPlus } from "lucide-react";
import { useSelector } from "react-redux";

const RaciAssignment = ({ initiallyExpanded = false }) => {
  const noneOption = { id: "__none__", label: "None" };
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [manualOpen, setManualOpen] = useState({
    accountable: false,
    consulted: false,
    informed: false,
  });
  const [manualValue, setManualValue] = useState({
    accountable: "",
    consulted: "",
    informed: "",
  });

  const { values, setFieldValue } = useFormikContext();

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const { findingsData } = useSelector((state) => state.organization);
  const currentUserEmail = useSelector((state) => state.user.email);

  const peopleOptions = useMemo(() => {
    const emails = new Set();
    if (currentUserEmail) emails.add(currentUserEmail);

    Object.values(findingsData || {}).forEach((f) => {
      const maybeAdd = (v) => {
        if (!v) return;
        if (Array.isArray(v)) v.forEach((x) => typeof x === "string" && x.trim() && emails.add(x.trim()));
        else if (typeof v === "string" && v.trim()) emails.add(v.trim());
      };
      maybeAdd(f.reporter);
      maybeAdd(f.responsible);
      maybeAdd(f.accountable);
      maybeAdd(f.consulted);
      maybeAdd(f.informed);
    });

    const maybeAddCurrent = (v) => {
      if (!v) return;
      if (Array.isArray(v)) {
        v.forEach((entry) => typeof entry === "string" && entry.trim() && emails.add(entry.trim()));
        return;
      }
      if (typeof v === "string" && v.trim()) emails.add(v.trim());
    };

    maybeAddCurrent(values?.responsible);
    maybeAddCurrent(values?.accountable);
    maybeAddCurrent(values?.consulted);
    maybeAddCurrent(values?.informed);

    return Array.from(emails)
      .sort((a, b) => a.localeCompare(b))
      .map((email) => ({ id: email, label: email }));
  }, [
    findingsData,
    currentUserEmail,
    values?.responsible,
    values?.accountable,
    values?.consulted,
    values?.informed,
  ]);
  const roleOptions = useMemo(
    () => [noneOption, ...peopleOptions],
    [noneOption, peopleOptions],
  );

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const getRoleValue = (role) => {
    const value = values?.[role];
    if (Array.isArray(value)) {
      return (
        value.find((entry) => typeof entry === "string" && entry.trim()) || ""
      );
    }
    return value || "";
  };

  const getRoleValueWithNone = (role) => {
    const value = getRoleValue(role);
    return value ? value : noneOption.id;
  };

  const setRoleValue = (role, rawEmail) => {
    const email = (rawEmail || "").trim();
    if (!email || email === noneOption.id || email === noneOption.label) {
      setFieldValue(role, []);
      return;
    }
    if (!emailRegex.test(email)) return;
    setFieldValue(role, [email]);
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>RACI Assignment</div>
        <div className={styles.subtitle}>
          Assign roles: Responsible (does the work), Accountable (final authority), Consulted (provides input), Informed (kept updated)
        </div>
      </div>

      <div className={styles.field}>
        <div className={styles.labelRow}>
          <div className={styles.label}>
            Responsible <span className={styles.required}>*</span>{" "}
            <span className={styles.hint}>(Who does the work)</span>
          </div>
        </div>
        <div className={styles.selectRowSingle}>
          <Field
            name="responsible"
            component={Select}
            options={peopleOptions}
            text="Select responsible person"
            className={styles.select}
            variant="modal"
          />
        </div>
      </div>

      <button type="button" className={styles.expandRow} onClick={toggleExpanded}>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        {isExpanded ? "Hide Additional Roles" : "Add Accountable, Consulted & Informed"}
      </button>

      {isExpanded && (
        <div className={styles.expandedFields}>
          <div className={styles.field}>
            <div className={styles.label}>
              Accountable <span className={styles.hint}>(Final authority/decision maker)</span>
            </div>
            <div className={styles.selectRow}>
              <Select
                options={roleOptions}
                text="None"
                value={getRoleValueWithNone("accountable")}
                variant="modal"
                className={styles.select}
                onChange={(id) => setRoleValue("accountable", id)}
              />
              <button
                type="button"
                className={styles.addPersonBtn}
                title="Add person"
                onClick={() =>
                  setManualOpen((prev) => ({ ...prev, accountable: !prev.accountable }))
                }
              >
                <UserPlus size={16} />
              </button>
            </div>
            {manualOpen.accountable && (
              <div className={styles.manualRow}>
                <input
                  className={styles.manualInput}
                  placeholder="Enter email"
                  value={manualValue.accountable}
                  onChange={(e) =>
                    setManualValue((prev) => ({ ...prev, accountable: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setRoleValue("accountable", manualValue.accountable);
                      setManualValue((prev) => ({ ...prev, accountable: "" }));
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.manualAddBtn}
                  onClick={() => {
                    setRoleValue("accountable", manualValue.accountable);
                    setManualValue((prev) => ({ ...prev, accountable: "" }));
                  }}
                >
                  Add
                </button>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.label}>
              Consulted <span className={styles.hint}>(Provides input/expertise)</span>
            </div>
            <div className={styles.selectRow}>
              <Select
                options={roleOptions}
                text="Add consulted person"
                value={getRoleValueWithNone("consulted")}
                variant="modal"
                className={styles.select}
                onChange={(id) => setRoleValue("consulted", id)}
              />
              <button
                type="button"
                className={styles.addPersonBtn}
                title="Add person"
                onClick={() =>
                  setManualOpen((prev) => ({ ...prev, consulted: !prev.consulted }))
                }
              >
                <UserPlus size={16} />
              </button>
            </div>
            {manualOpen.consulted && (
              <div className={styles.manualRow}>
                <input
                  className={styles.manualInput}
                  placeholder="Enter email"
                  value={manualValue.consulted}
                  onChange={(e) =>
                    setManualValue((prev) => ({ ...prev, consulted: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setRoleValue("consulted", manualValue.consulted);
                      setManualValue((prev) => ({ ...prev, consulted: "" }));
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.manualAddBtn}
                  onClick={() => {
                    setRoleValue("consulted", manualValue.consulted);
                    setManualValue((prev) => ({ ...prev, consulted: "" }));
                  }}
                >
                  Add
                </button>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.label}>
              Informed <span className={styles.hint}>(Kept in the loop)</span>
            </div>
            <div className={styles.selectRow}>
              <Select
                options={roleOptions}
                text="Add informed person"
                value={getRoleValueWithNone("informed")}
                variant="modal"
                className={styles.select}
                onChange={(id) => setRoleValue("informed", id)}
              />
              <button
                type="button"
                className={styles.addPersonBtn}
                title="Add person"
                onClick={() =>
                  setManualOpen((prev) => ({ ...prev, informed: !prev.informed }))
                }
              >
                <UserPlus size={16} />
              </button>
            </div>
            {manualOpen.informed && (
              <div className={styles.manualRow}>
                <input
                  className={styles.manualInput}
                  placeholder="Enter email"
                  value={manualValue.informed}
                  onChange={(e) =>
                    setManualValue((prev) => ({ ...prev, informed: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setRoleValue("informed", manualValue.informed);
                      setManualValue((prev) => ({ ...prev, informed: "" }));
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.manualAddBtn}
                  onClick={() => {
                    setRoleValue("informed", manualValue.informed);
                    setManualValue((prev) => ({ ...prev, informed: "" }));
                  }}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RaciAssignment;
